<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import type { WidgetView } from '@support/widget-contracts';

import ConversationScreen from '@/components/ConversationScreen.vue';
import TicketListScreen from '@/components/TicketListScreen.vue';
import {
  contact,
  isRenewingIdentity,
  retrySession,
  sessionErrorMessage,
  sessionStatus,
  startSession,
} from '@/api/session';
import type { WidgetTicket } from '@/api/types';

// The `open` message may name a view. `conversation` names a screen but not a
// ticket, and the host has no way to point at one yet, so both views land on
// the list; kept in the props so the contract stays visible until deep-linking
// exists.
defineProps<{
  view: WidgetView;
}>();

defineEmits<{
  close: [];
}>();

const activeTicket = ref<WidgetTicket | null>(null);

const identity = computed(() => {
  const current = contact.value;

  if (!current) {
    return null;
  }

  return current.name && current.email
    ? `${current.name} · ${current.email}`
    : current.name ?? current.email ?? 'your account';
});

onMounted(() => {
  void startSession();
});
</script>

<template>
  <div class="panel">
    <header class="header">
      <button
        v-if="activeTicket"
        data-testid="widget-back"
        type="button"
        class="icon-button"
        aria-label="Back to conversations"
        @click="activeTicket = null"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="m15 18-6-6 6-6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <h1
        data-testid="widget-title"
        class="header__title"
      >
        {{ activeTicket ? activeTicket.title : 'Hope.Cloud support' }}
      </h1>

      <button
        type="button"
        class="icon-button"
        aria-label="Close support"
        @click="$emit('close')"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M18 6 6 18M6 6l12 12"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </header>

    <p
      v-if="identity"
      data-testid="widget-identity"
      class="identity"
    >
      Signed in as {{ identity }}
    </p>

    <!-- Renewal is surfaced WITHOUT unmounting the screen below it: a reply the
         visitor has typed sits in a composer, and dropping that to show a status
         line would lose their words in order to fix our token. -->
    <p
      v-if="isRenewingIdentity && sessionStatus !== 'loading'"
      data-testid="widget-renewal-banner"
      class="banner"
    >
      Reconnecting…
    </p>

    <p
      v-if="sessionStatus === 'loading'"
      data-testid="widget-connecting"
      class="state"
    >
      {{ isRenewingIdentity ? 'Reconnecting…' : 'Connecting…' }}
    </p>

    <div
      v-else-if="sessionStatus === 'error'"
      data-testid="widget-error"
      class="state state--error"
    >
      <p
        data-testid="widget-error-message"
        class="state__message"
      >
        {{ sessionErrorMessage }}
      </p>
      <button
        type="button"
        class="state__retry"
        @click="retrySession"
      >
        Try again
      </button>
    </div>

    <ConversationScreen
      v-else-if="activeTicket"
      :key="activeTicket.id"
      :ticket="activeTicket"
    />

    <TicketListScreen
      v-else
      @select="activeTicket = $event"
    />
  </div>
</template>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 14px;
  background: var(--w-bg);
  box-shadow: var(--w-shadow);
  overflow: hidden;
}

.header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 12px 12px 16px;
  border-bottom: 1px solid var(--w-border);
}

.header__title {
  flex: 1;
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--w-text-muted);
}

.icon-button:hover {
  background: var(--w-bg-subtle);
  color: var(--w-text);
}

.identity {
  margin: 0;
  padding: 8px 16px;
  border-bottom: 1px solid var(--w-border);
  font-size: 12px;
  color: var(--w-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.state {
  margin: 0;
  padding: 16px;
  font-size: 13px;
  color: var(--w-text-muted);
}

.banner {
  margin: 0;
  padding: 6px 16px;
  border-bottom: 1px solid var(--w-border);
  background: var(--w-bg-subtle);
  font-size: 12px;
  color: var(--w-text-muted);
}

.state--error {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.state__message {
  margin: 0;
  color: var(--w-danger);
}

.state__retry {
  padding: 6px 12px;
  border: none;
  border-radius: 8px;
  background: var(--w-bg-subtle);
  color: var(--w-text);
  font-weight: 600;
}
</style>
